// src/content/fiches.ts
//
// Phase 2 catalogue: 8 fiche templates as FicheDef (Omit<Fiche, 'id'>).
// `id` is assigned per-instance when decks are built in Phase 4.
// Pure data — no runtime behavior. Effect logic lands in Phase 3/8.
//
// All chips/mult/effect values are sourced verbatim from
// .planning/phases/02-data-model-content-catalogues/02-CONTEXT.md.

import type { FicheDef } from '../state/types';
import type { ChessPiece } from '../systems/chess';

// Lucky 7: spec wording is "×3 total" on slot.number === 7.
// With base mult=1, bonus=2 produces final mult of (1+2)=3 → 3× total. (Per CONTEXT.md decision.)
export const FICHES: readonly FicheDef[] = Object.freeze([
  {
    defId: 'standard',
    name: 'Standard',
    description: 'Plain fiche. 10 chips × 1 mult.',
    chips: 10,
    mult: 1,
    rarity: 'common',
  },
  {
    defId: 'lucky7',
    name: 'Lucky 7',
    // ×3 total when slot.number === 7. Encoded as bonus=2 since (1 + 2) × 1 = 3 final mult.
    description: 'If slot.number is 7, mult ×3.',
    chips: 10,
    mult: 1,
    rarity: 'common',
    effect: { type: 'plusMultIfNumber', number: 7, bonus: 2 },
  },
  {
    defId: 'red_hot',
    name: 'Red Hot',
    description: 'On red hits, +2 mult.',
    chips: 8,
    mult: 2,
    rarity: 'common',
    effect: { type: 'plusMultIfColor', color: 'red', bonus: 2 },
  },
  {
    defId: 'cold_steel',
    name: 'Cold Steel',
    description: 'On black hits, +2 mult.',
    chips: 8,
    mult: 2,
    rarity: 'common',
    effect: { type: 'plusMultIfColor', color: 'black', bonus: 2 },
  },
  {
    defId: 'even_steven',
    name: 'Even Steven',
    description: '+1 mult per covered even bet on this spin.',
    chips: 12,
    mult: 1,
    rarity: 'uncommon',
    effect: { type: 'plusMultPerCoveredEven', bonus: 1 },
  },
  {
    defId: 'gambler',
    name: 'Gambler',
    description: 'Only scores when placed on a straight bet; otherwise contributes 0.',
    chips: 5,
    mult: 5,
    rarity: 'uncommon',
    effect: { type: 'doubleIfStraight' },
  },
  {
    defId: 'pair_o_dice',
    name: "Pair o' Dice",
    description: '+3 mult per other Pair o’ Dice in the tray this spin.',
    chips: 6,
    mult: 1,
    rarity: 'uncommon',
    effect: { type: 'plusMultPerSameDefInTray', bonus: 3 },
  },
  {
    defId: 'house_special',
    name: 'House Special',
    description: 'Loses 50% chips on red hits, gains 50% chips on black hits.',
    chips: 25,
    mult: 3,
    rarity: 'rare',
    // House Special spec wording: "Loses 50% chips on red, gains 50% chips on black".
    // The single-effect type encodes the red LOSS leg explicitly; the Phase 3 scoring
    // resolver must also apply the +50% chips gain when slot.color === 'black' for this
    // defId (either via a dual-condition effect variant or per-defId special case).
    effect: { type: 'plusChipsIfColor', color: 'red', sign: '-', percent: 50 },
  },
  // Phase 14: Chess Fiche defs (CHESS-03)
  // Per CONTEXT.md: pawn=common, knight=uncommon, bishop=uncommon, rook=rare,
  // king=rare, queen=legendary. Piece values (chips/mult) give chess queen
  // the best raw stats to justify the legendary rarity band.
  {
    defId: 'chess_pawn',
    name: 'Chess Pawn',
    description: 'Covers all squares a pawn attacks from this cell.',
    chips: 8,
    mult: 1,
    rarity: 'common',
    effect: { type: 'chessMove', piece: 'pawn' as ChessPiece },
  },
  {
    defId: 'chess_knight',
    name: 'Chess Knight',
    description: 'Covers all 8 L-shaped squares from this cell.',
    chips: 10,
    mult: 1,
    rarity: 'uncommon',
    effect: { type: 'chessMove', piece: 'knight' as ChessPiece },
  },
  {
    defId: 'chess_bishop',
    name: 'Chess Bishop',
    description: 'Covers every diagonal cell from here to the edge.',
    chips: 10,
    mult: 2,
    rarity: 'uncommon',
    effect: { type: 'chessMove', piece: 'bishop' as ChessPiece },
  },
  {
    defId: 'chess_rook',
    name: 'Chess Rook',
    description: 'Covers every row and column cell from here.',
    chips: 12,
    mult: 2,
    rarity: 'rare',
    effect: { type: 'chessMove', piece: 'rook' as ChessPiece },
  },
  {
    defId: 'chess_king',
    name: 'Chess King',
    description: 'Covers all 8 adjacent squares from this cell.',
    chips: 12,
    mult: 2,
    rarity: 'rare',
    effect: { type: 'chessMove', piece: 'king' as ChessPiece },
  },
  {
    defId: 'chess_queen',
    name: 'Chess Queen',
    description: 'Covers all diagonal and orthogonal cells. Power move.',
    chips: 15,
    mult: 3,
    rarity: 'legendary',
    effect: { type: 'chessMove', piece: 'queen' as ChessPiece },
  },
  // Phase 16 (FICHE-12): prev-spin-state fiches
  {
    defId: 'hot_streak_rider',
    name: 'Hot Streak Rider',
    description: 'If last spin hit, +3 mult.',
    chips: 10,
    mult: 1,
    rarity: 'uncommon',
    effect: { type: 'plusMultIfPrevSpinHit', bonus: 3 },
  },
  {
    defId: 'chain_gambler',
    name: 'Chain Gambler',
    description: '+5 chips per current streak length.',
    chips: 8,
    mult: 1,
    rarity: 'uncommon',
    effect: { type: 'plusChipsPerStreak', chipsPerStreak: 5 },
  },
  {
    defId: 'zero_chaser',
    name: 'Zero Chaser',
    description: 'If last spin landed on 0, +40 chips.',
    chips: 12,
    mult: 1,
    rarity: 'rare',
    effect: { type: 'bonusIfPrevSpinZero', bonusChips: 40 },
  },
  // Phase 16 (FICHE-15): standard-fiche-count synergy
  {
    defId: 'fiche_hoarder',
    name: 'Fiche Hoarder',
    description: '+3 chips per standard fiche in your deck.',
    chips: 10,
    mult: 1,
    rarity: 'uncommon',
    effect: { type: 'standardFicheCountBonus', chipsPerStandard: 3 },
  },
  // Phase 19 (FICHE-19): Cell Swapper — on resolve, swaps the placed cell's
  // position with a random orthogonal neighbor for the rest of the run.
  // Mutation handled in RoundScene post-score dispatch (plan 19-02 task 3).
  // Stat profile: 8 chips × 1 mult, common rarity. Modest baseline so the
  // value comes from the swap, not the raw score.
  {
    defId: 'cell_swapper',
    name: 'Cell Swapper',
    description:
      'On resolve, swaps this cell with a random adjacent cell on the board for the rest of the run.',
    chips: 8,
    mult: 1,
    rarity: 'common',
    effect: { type: 'cellSwap' },
  },
  // Phase 23 (FICHE-23): Tetris fiches. Each covers a multi-cell shape from
  // the anchor cell (top-left of bounding box). [drow, dcol] offsets are
  // non-negative so the anchor always sits inside the bounding rectangle.
  {
    defId: 'tetris_i',
    name: 'Tetris I',
    description: '4 cells in a row. Pivot is the second cell from the left.',
    chips: 8,
    mult: 1,
    rarity: 'common',
    effect: {
      type: 'tetrisShape',
      // Pivot at [0,0]; extends one left and two right.
      shape: [
        [0, -1],
        [0, 0],
        [0, 1],
        [0, 2],
      ],
    },
  },
  {
    defId: 'tetris_o',
    name: 'Tetris O',
    description: '2×2 square. Pivot is the top-left cell.',
    chips: 8,
    mult: 1,
    rarity: 'common',
    effect: {
      type: 'tetrisShape',
      // Pivot at [0,0]; fills the 2×2 block to the right and below.
      shape: [
        [0, 0],
        [0, 1],
        [1, 0],
        [1, 1],
      ],
    },
  },
  {
    defId: 'tetris_t',
    name: 'Tetris T',
    description: 'T-shape. Pivot is the top-middle cell.',
    chips: 9,
    mult: 1,
    rarity: 'uncommon',
    effect: {
      type: 'tetrisShape',
      // Pivot at [0,0]; one cell left, one right, one below pivot.
      shape: [
        [0, -1],
        [0, 0],
        [0, 1],
        [1, 0],
      ],
    },
  },
  {
    defId: 'tetris_s',
    name: 'Tetris S',
    description: 'S-shape. Pivot is the left cell of the top pair.',
    chips: 9,
    mult: 1,
    rarity: 'uncommon',
    effect: {
      type: 'tetrisShape',
      // Pivot at [0,0]: top pair is [0,0]+[0,1]; bottom pair is [1,-1]+[1,0].
      shape: [
        [0, 0],
        [0, 1],
        [1, -1],
        [1, 0],
      ],
    },
  },
  {
    defId: 'tetris_z',
    name: 'Tetris Z',
    description: 'Z-shape. Pivot is the right cell of the top pair.',
    chips: 9,
    mult: 1,
    rarity: 'uncommon',
    effect: {
      type: 'tetrisShape',
      // Pivot at [0,0]: top pair is [0,-1]+[0,0]; bottom pair is [1,0]+[1,1].
      shape: [
        [0, -1],
        [0, 0],
        [1, 0],
        [1, 1],
      ],
    },
  },
  {
    defId: 'tetris_j',
    name: 'Tetris J',
    description: 'J-shape. Pivot is the corner where the column meets the hook.',
    chips: 10,
    mult: 1,
    rarity: 'rare',
    effect: {
      type: 'tetrisShape',
      // Pivot at [0,0] = corner cell; column extends two above, hook one right.
      shape: [
        [-2, 0],
        [-1, 0],
        [0, 0],
        [0, 1],
      ],
    },
  },
  {
    defId: 'tetris_l',
    name: 'Tetris L',
    description: 'L-shape. Pivot is the corner where the column meets the hook.',
    chips: 10,
    mult: 1,
    rarity: 'rare',
    effect: {
      type: 'tetrisShape',
      // Pivot at [0,0] = corner cell; column extends two above, hook one left.
      shape: [
        [-2, 0],
        [-1, 0],
        [0, -1],
        [0, 0],
      ],
    },
  },
] as const);

export const FICHES_BY_ID: Readonly<Record<string, FicheDef>> = Object.freeze(
  FICHES.reduce<Record<string, FicheDef>>((acc, def) => {
    acc[def.defId] = def;
    return acc;
  }, {}),
);

/** Spritesheet frame index for each tetris fiche (key = 'tetris-fiches'). */
export const TETRIS_FICHE_FRAME: Readonly<Record<string, number>> = {
  tetris_i: 0,
  tetris_o: 1,
  tetris_t: 2,
  tetris_s: 3,
  tetris_z: 4,
  tetris_j: 5,
  tetris_l: 6,
};

/** Spritesheet frame index for each chess fiche (key = 'chess-fiches'). */
export const CHESS_FICHE_FRAME: Readonly<Record<string, number>> = {
  chess_pawn: 0,
  chess_knight: 1,
  chess_bishop: 2,
  chess_rook: 3,
  chess_queen: 4,
  chess_king: 5,
};

const MISC_FICHE_FRAME_1: Readonly<Record<string, number>> = {
  standard: 0,
  lucky7: 1,
  red_hot: 2,
  cold_steel: 3,
  even_steven: 4,
  gambler: 5,
};

const MISC_FICHE_FRAME_2: Readonly<Record<string, number>> = {
  pair_o_dice: 0,
  house_special: 1,
  hot_streak_rider: 2,
  chain_gambler: 3,
  zero_chaser: 4,
  fiche_hoarder: 5,
};

const MISC_FICHE_FRAME_3: Readonly<Record<string, number>> = {
  cell_swapper: 0,
  cold_front: 1,
  column_shark: 2,
  corner_king: 3,
  dozen_devotee: 4,
  split_shot: 5,
};

/**
 * Unified spritesheet lookup for all fiches that use a spritesheet rather than
 * an individual PNG. Returns { key, frame } or undefined if the fiche uses a
 * per-fiche sprite (or falls back to chip-rarity).
 */
export function ficheSpritesheet(defId: string): { key: string; frame: number } | undefined {
  const tf = TETRIS_FICHE_FRAME[defId];
  if (tf !== undefined) return { key: 'tetris-fiches', frame: tf };
  const cf = CHESS_FICHE_FRAME[defId];
  if (cf !== undefined) return { key: 'chess-fiches', frame: cf };
  const m1 = MISC_FICHE_FRAME_1[defId];
  if (m1 !== undefined) return { key: 'misc-fiches-1', frame: m1 };
  const m2 = MISC_FICHE_FRAME_2[defId];
  if (m2 !== undefined) return { key: 'misc-fiches-2', frame: m2 };
  const m3 = MISC_FICHE_FRAME_3[defId];
  if (m3 !== undefined) return { key: 'misc-fiches-3', frame: m3 };
  return undefined;
}
